import React, { useRef, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import { sharedExportarPdf } from "../utils/shared-functions";
import { obtenerListaGrupoGeocerca } from "../api/api-grupo-geocerca";
import { obtenerEntradaSalidaGeocerca } from "../api/api-seguimiento";
import { ringsFromGeocercaItem } from "./mapa-geocercas-cliente";
import MapaGeocercasEntradaSalida from "./mapa-geocercas-entrada-salida";
import moment from "moment";

export default function EntradaSalidaGeocercaModal({ isOpen, onClose, data = null }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const contentRef = useRef(null);
    const [infoGeocerca, setInfoGeocerca] = useState(null);
    const [idGrupoGeocerca, setIdGrupoGeocerca] = useState(null);
    const [tabEntradaSalida, setTabEntradaSalida] = useState("lista");

    const geocercasParaMapa = useMemo(() => {
        if (!Array.isArray(infoGeocerca) || infoGeocerca.length === 0) {
            return [];
        }
        const seen = new Set();
        const out = [];
        for (const row of infoGeocerca) {
            const name =
                row.nombreGeocerca != null ? String(row.nombreGeocerca).trim() : "";
            if (name !== "" && seen.has(name)) {
                continue;
            }
            if (ringsFromGeocercaItem(row).length === 0) {
                continue;
            }
            if (name !== "") {
                seen.add(name);
            }
            out.push(row);
        }
        return out;
    }, [infoGeocerca]);

    useEffect(() => {
        if (!isOpen) {
            setInfoGeocerca(null);
            setIdGrupoGeocerca(null);
            return;
        }
        setTabEntradaSalida("lista");
        if (data !== null) {
            obtenerDatos();
        }
    }, [isOpen]);

    const obtenerDatos = async () => {
        try {
            let idGrupoGeocerca = null;
            const response = await obtenerListaGrupoGeocerca({}, false, t);
            // console.log('response');
            // console.log(response);
            if (Array.isArray(response) && response.length > 0) {
                idGrupoGeocerca = response[0].idGrupo;
                setIdGrupoGeocerca(idGrupoGeocerca);
            } else {
                setIdGrupoGeocerca(null);
            }
            if (idGrupoGeocerca !== null) {
                let objEnviar = {};
                objEnviar.idGrupoGeocerca = idGrupoGeocerca;
                objEnviar.Fecha = data.fechaEntrega;
                objEnviar.Placa = data.valuePlaca;
                objEnviar.idEmpresa = data.idEmpresa;
                const responseGeocerca = await obtenerEntradaSalidaGeocerca(objEnviar);
                // console.log('responseGeocerca');
                // console.log(responseGeocerca);
                if (responseGeocerca.error === false) {
                    setInfoGeocerca(responseGeocerca.data);
                }
            }
        } catch (error) {
            console.log('obtenerDatos err');
            console.log(error);
        }
    }
    if (!isOpen) return null;

    const handleOutsideClick = (e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
            onClose();
        }
    };

    const renderCuerpoGeocerca = () => {
        try {
            if (!Array.isArray(infoGeocerca) || infoGeocerca.length === 0) {
                return (
                    <p className="text-gray-500 text-sm m-0 leading-normal">
                        {t("label_no_hay_informacion_disponible")}
                    </p>
                );
            }

            return (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full max-w-full text-sm border-collapse table-fixed">
                        <colgroup>
                            <col style={{ width: "32%" }} />
                            <col style={{ width: "23%" }} />
                            <col style={{ width: "23%" }} />
                            <col style={{ width: "22%" }} />
                        </colgroup>
                        <thead>
                            <tr className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                                <th scope="col" className="text-left py-2 px-3 font-semibold">
                                    {t("column_geocerca")}
                                </th>
                                <th scope="col" className="text-right py-2 px-3 font-semibold">
                                    {t("column_fecha_hora_entrada_geocerca")}
                                </th>
                                <th scope="col" className="text-right py-2 px-3 font-semibold">
                                    {t("column_fecha_hora_salida_geocerca")}
                                </th>
                                <th scope="col" className="text-right py-2 px-3 font-semibold">
                                    {t("column_tiempo_estadia_geocerca")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {infoGeocerca.map((row, idx) => (
                                <tr
                                    key={`${row.nombreGeocerca}-${row.fechaHoraIngreso}-${idx}`}
                                    className="border-b border-gray-100"
                                >
                                    <td className="py-2 px-3 align-middle text-gray-900">
                                        <span className="inline-flex items-center gap-2 min-w-0">
                                            <span
                                                className="h-3 w-3 shrink-0 rounded-sm border border-gray-200"
                                                style={{
                                                    backgroundColor:
                                                        row.colorGeocerca || "#ccc",
                                                }}
                                                aria-hidden
                                            />
                                            <span className="break-words leading-normal">
                                                {row.nombreGeocerca}
                                            </span>
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 align-top text-right tabular-nums text-gray-800 whitespace-nowrap leading-normal">
                                        {row.fechaHoraIngreso}
                                    </td>
                                    <td className="py-2 px-3 align-top text-right tabular-nums text-gray-800 whitespace-nowrap leading-normal">
                                        {row.fechaHoraSalida}
                                    </td>
                                    <td className="py-2 px-3 align-top text-right tabular-nums font-medium text-gray-900 whitespace-nowrap leading-normal">
                                        {row.tiempoEstadia}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        } catch (error) {
            console.log('renderCuerpoGeocerca err');
            console.log(error);
            return null;
        }
    };

    return (
        <div
            className="fixed z-[60] inset-0 flex items-center justify-center p-4"
            onMouseDown={handleOutsideClick}
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/10" />

            {/* Modal */}
            <div
                ref={modalRef}
                className="relative bg-white rounded-lg shadow-lg w-[calc(100%-1.5rem)] max-w-[8.5in] sm:w-[calc(100%-2.5rem)] max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-lg font-semibold">
                        {t("label_detalle_salida_entrada_geocerca")}
                    </h2>
                    <button
                        type="button"
                        onClick={() => sharedExportarPdf(contentRef, 'Geofence-Entry-Exit')}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
                    >
                        <FileDown className="h-4 w-4" />
                        {t("label_exportar_pdf")}
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="bg-white text-black p-6 overflow-y-auto"
                    ref={contentRef}
                    style={{
                        fontFamily: "sans-serif",
                    }}
                >
                    {data === null ? (
                        <p className="text-gray-500 text-sm" data-pdf-block>
                            {t("label_no_hay_informacion_disponible")}
                        </p>
                    ) : (
                        <div data-pdf-block className="space-y-3">
                            <p className="text-gray-500 text-sm m-0 py-2 leading-normal tabular-nums">
                                {data.labelPlaca} -{' '}
                                {moment(data.fechaEntrega).format('YYYY/MM/DD')}
                            </p>

                            <div
                                className="flex gap-1 border-b border-gray-200"
                                role="tablist"
                                aria-label={t("label_detalle_salida_entrada_geocerca")}
                            >
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tabEntradaSalida === "lista"}
                                    onClick={() => setTabEntradaSalida("lista")}
                                    className={
                                        tabEntradaSalida === "lista"
                                            ? "border-b-2 border-blue-500 px-3 py-2 text-sm font-medium text-blue-600"
                                            : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                                    }
                                >
                                    {t("label_tabla")}
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tabEntradaSalida === "mapa"}
                                    onClick={() => setTabEntradaSalida("mapa")}
                                    className={
                                        tabEntradaSalida === "mapa"
                                            ? "border-b-2 border-blue-500 px-3 py-2 text-sm font-medium text-blue-600"
                                            : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                                    }
                                >
                                    {t("label_mapa")}
                                </button>
                            </div>

                            {tabEntradaSalida === "lista" ? (
                                renderCuerpoGeocerca()
                            ) : geocercasParaMapa.length === 0 ? (
                                <p className="text-gray-500 text-sm m-0 leading-normal">
                                    {t("label_no_hay_informacion_disponible")}
                                </p>
                            ) : (
                                <MapaGeocercasEntradaSalida
                                    geocercas={geocercasParaMapa}
                                    idGrupoGeocerca={idGrupoGeocerca}
                                    fechaEntrega={data.fechaEntrega}
                                    valuePlaca={data.valuePlaca}
                                    idEmpresa={data.idEmpresa}
                                />
                            )}
                        </div>
                    )}


                </div>

                {/* Footer */}
                <div className="p-6 border-t flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                    >
                        {t("label_cerrar")}
                    </button>
                </div>

            </div>
        </div>
    );
}